import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, AlertTriangle, DollarSign, Eye } from "lucide-react";
import { API } from "@/lib/constants";

/**
 * Phase 5 Day 10 — VLM Critic Mission Control digest.
 *
 * Spec ref: § 3.2 + Remi's Day 10 specifics (Feb 27 2026).
 *
 * Three regions:
 *   1. Per-dimension rollup (Last 7 days).
 *   2. Per-dimension drift widget — amber "drift" pill ONLY when
 *      disagreement > 25% AND sample_n >= 20. Below sample_n the
 *      pill suppresses so early-publish noise doesn't trigger false
 *      alerts (Remi's specific Feb 27 gate).
 *   3. Cost-guard throttle state (70/90/100% bands).
 *   4. Recent observations list (operator-readable, <80 char each).
 *
 * The drift widget reads the /retro-evaluate endpoint's new
 * `structural_disagreement_rate` field (post-Day 10 acknowledge
 * weighting) — that's the sharper signal Phase 5.1's promotion gate
 * actually needs.
 */

const DIMENSIONS = [
  { key: "focal_point", label: "Focal point" },
  { key: "hierarchy", label: "Hierarchy" },
  { key: "brand_presence", label: "Brand presence" },
  { key: "repetition", label: "Repetition" },
];

const DRIFT_DISAGREEMENT_THRESHOLD = 0.25;
const DRIFT_SAMPLE_N_MIN = 20;

function CostGuardBand({ budget }) {
  if (!budget) return null;
  const pct = (budget.spent_pct || 0) * 100;
  let color = "emerald";
  let label = "Clear";
  if (pct >= 100) { color = "red"; label = "Disabled"; }
  else if (pct >= 90) { color = "amber"; label = "Critical (90%)"; }
  else if (pct >= 70) { color = "amber"; label = "Warning (70%)"; }
  return (
    <div className={`rounded border border-${color}-200 bg-${color}-50 px-3 py-2`} data-testid="digest-cost-guard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className={`h-4 w-4 text-${color}-700`} />
          <span className={`text-xs font-semibold text-${color}-900`}>Budget · {label}</span>
        </div>
        <div className="text-[11px] tabular-nums text-slate-700">
          ${Number(budget.spent_usd || 0).toFixed(2)} / ${Number(budget.ceiling_usd || 0).toFixed(0)} · {pct.toFixed(1)}%
        </div>
      </div>
      <div className="mt-1.5 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full bg-${color}-500`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

function DriftPill({ retro }) {
  if (!retro) return null;
  const overSampleFloor = retro.sample_n >= DRIFT_SAMPLE_N_MIN;
  const overDisagreement = (retro.structural_disagreement_rate || 0) > DRIFT_DISAGREEMENT_THRESHOLD;
  if (!overSampleFloor) {
    return (
      <span data-testid="digest-drift-insufficient-data" className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500" title="Need ≥20 samples to surface drift signal">
        n={retro.sample_n}
      </span>
    );
  }
  if (overDisagreement) {
    return (
      <span data-testid="digest-drift-detected" className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
        <AlertTriangle className="h-3 w-3" />
        drift · {(retro.structural_disagreement_rate * 100).toFixed(0)}%
      </span>
    );
  }
  return (
    <span data-testid="digest-drift-stable" className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
      stable · n={retro.sample_n}
    </span>
  );
}

export default function VlmCriticDigest() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [observations, setObservations] = useState([]);
  const [budget, setBudget] = useState(null);
  const [retroByDim, setRetroByDim] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const base = `${API}/v2/workspaces/${workspaceId}/critic`;
      const [d, o, b, ...retros] = await Promise.all([
        fetch(`${base}/digest?days=7`, { credentials: "include" }),
        fetch(`${base}/observations?days=7&limit=20`, { credentials: "include" }),
        fetch(`${base}/budget`, { credentials: "include" }),
        ...DIMENSIONS.map((d) =>
          fetch(`${base}/calibration/retro-evaluate?dimension=${d.key}&window=50`, { credentials: "include" })
        ),
      ]);
      setDigest(d.ok ? await d.json() : null);
      setObservations(o.ok ? await o.json() : []);
      setBudget(b.ok ? await b.json() : null);
      const r = {};
      for (let i = 0; i < DIMENSIONS.length; i++) {
        r[DIMENSIONS[i].key] = retros[i].ok ? await retros[i].json() : null;
      }
      setRetroByDim(r);
    } catch (e) {
      console.error("digest fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="digest-back-btn">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <button onClick={fetchAll} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="digest-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-slate-900">VLM Critic — Last 7 Days</h1>
        <p className="text-xs text-slate-600 mt-1">
          Per-dimension rollup, drift signal, cost-guard state, and recent operator observations.
        </p>
      </div>

      {loading && <div className="text-xs text-slate-500" data-testid="digest-loading">Loading…</div>}

      {!loading && (
        <>
          <CostGuardBand budget={budget} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="digest-dimension-grid">
            {DIMENSIONS.map((dim) => {
              const counts = digest?.per_dimension?.[dim.key] || { assessed: 0, matched: 0, flagged: 0 };
              const retro = retroByDim[dim.key];
              return (
                <div key={dim.key} data-testid={`digest-dimension-${dim.key.replace(/_/g, "-")}`} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-800">{dim.label.toUpperCase()}</span>
                    <DriftPill retro={retro} />
                  </div>
                  <div className="mt-2 flex items-baseline gap-3 text-xs tabular-nums">
                    <span><span className="font-semibold text-slate-700">{counts.assessed}</span> <span className="text-slate-500">assessed</span></span>
                    <span><span className="font-semibold text-emerald-700">{counts.matched}</span> <span className="text-slate-500">on intent</span></span>
                    <span><span className="font-semibold text-amber-700">{counts.flagged}</span> <span className="text-slate-500">flagged</span></span>
                  </div>
                  {retro && retro.sample_n >= DRIFT_SAMPLE_N_MIN && (
                    <div className="mt-2 text-[10px] text-slate-500 tabular-nums">
                      retro: {retro.acknowledged_ship_count} ack-ship · {retro.silent_ship_count} silent-ship · {retro.sample_n} samples
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="digest-observations">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-slate-500" />
              <span className="text-xs font-semibold text-slate-800">Recent observations</span>
              {digest?.acknowledgement_count > 0 && (
                <span className="ml-auto text-[10px] text-slate-500">
                  · {digest.acknowledgement_count} acknowledgement{digest.acknowledgement_count === 1 ? "" : "s"} in window
                </span>
              )}
            </div>
            {observations.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No critic assessments in the last 7 days.</p>
            ) : (
              <ul className="space-y-2">
                {observations.map((obs, i) => (
                  <li key={i} data-testid={`digest-observation-${i}`} className="flex items-start gap-2 text-xs">
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${obs.matches_intent ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-700">{obs.observation}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {obs.card_title || obs.card_id?.slice(0, 8)} · {obs.dimension} · {obs.assessed_at?.slice(0, 10)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-[10px] text-slate-400 text-center pt-2">
            Drift pill activates at disagreement &gt; 25% AND sample_n ≥ 20. Below that, the signal is statistical noise.
          </div>
        </>
      )}
    </div>
  );
}
